/**
 * StepGraph — vertical node list used by Run History (run trace) and
 * Strategies (rule graph). Translates comp `.graph` / `.gstep` / `.gnode`
 * styles to semantic Tailwind.
 *
 * Each node shows a status dot/icon, connector line, name, optional kind
 * chip + meta (duration/cost/retries) and optional sub-content. Failed
 * nodes get a danger left-bar. Rows are selectable.
 */
import React from 'react';
import { Check, X, Clock, Zap, Loader2 } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StepStatus = 'ok' | 'running' | 'failed' | 'skipped' | 'pending';

export interface StepGraphStep {
  id: string;
  /** Node name — string or ReactNode for rich content */
  name: React.ReactNode;
  status?: StepStatus;
  /** Short chip label e.g. "tool", "llm", "condition" */
  kind?: string;
  /** Right-aligned meta line — duration, cost, retries */
  meta?: React.ReactNode;
  /** Optional sub-row content (description, error snippet) */
  sub?: React.ReactNode;
}

export interface StepGraphProps {
  steps: StepGraphStep[];
  /** Highlight this step id as selected */
  activeId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

// ─── Status node ─────────────────────────────────────────────────────────────

/**
 * Maps StepStatus to the node box + icon.
 * Comp: .gnode.ok → success-muted bg + success-text color, etc.
 */
function StatusNode({ status }: { status: StepStatus }) {
  const base = 'w-[26px] h-[26px] rounded-lg flex items-center justify-center shrink-0 z-10';

  switch (status) {
    case 'ok':
      return (
        <span className={`${base} bg-success-muted text-success`}>
          <Check size={13} />
        </span>
      );
    case 'running':
      return (
        <span className={`${base} bg-accent-muted text-accent-text`}>
          <Loader2 size={13} className="animate-spin" />
        </span>
      );
    case 'failed':
      return (
        <span className={`${base} bg-danger-muted text-danger`}>
          <X size={13} />
        </span>
      );
    case 'skipped':
      return (
        <span className={`${base} bg-surface-alt text-faint border border-dashed border-strong`}>
          <Clock size={13} />
        </span>
      );
    default:
      return (
        <span className={`${base} bg-surface-alt text-faint border border-dashed border-strong`}>
          <Clock size={13} />
        </span>
      );
  }
}

// ─── Kind chip ────────────────────────────────────────────────────────────────

function KindChip({ kind }: { kind: string }) {
  return <span className="font-mono text-[10px] text-faint">{kind}</span>;
}

// ─── StepGraph ────────────────────────────────────────────────────────────────

export function StepGraph({ steps, activeId, onSelect, className = '' }: StepGraphProps) {
  if (steps.length === 0) return null;

  return (
    <div className={`py-2 ${className}`}>
      {steps.map((step, i) => {
        const status = step.status ?? 'pending';
        const isFailed = status === 'failed';
        const isRunning = status === 'running';
        const isSelected = step.id === activeId;
        const isFirst = i === 0;
        const isLast = i === steps.length - 1;

        return (
          <div
            key={step.id}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onClick={() => onSelect?.(step.id)}
            onKeyDown={(e) => {
              if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onSelect(step.id);
              }
            }}
            className={[
              'grid gap-3 px-4 py-[11px] items-center relative',
              /* connector line — absolute positioned pseudo-equivalent */
              'before:content-[""] before:absolute before:left-[29px] before:w-[2px] before:bg-border before:z-0',
              isFirst
                ? 'before:top-1/2 before:bottom-[-8px]'
                : isLast
                  ? 'before:top-[-8px] before:bottom-1/2'
                  : 'before:top-[-8px] before:bottom-[-8px]',
              isFailed ? 'shadow-[inset_3px_0_0] shadow-danger' : '',
              isSelected ? 'bg-surface-hover' : '',
              onSelect ? 'cursor-pointer hover:bg-surface-hover transition-colors' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ gridTemplateColumns: '28px 1fr auto' }}
          >
            {/* Node icon */}
            <StatusNode status={status} />

            {/* Main content */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={['text-[13px] font-medium', isFailed ? 'text-danger' : 'text-heading'].join(' ')}>
                  {step.name}
                </span>
                {step.kind && <KindChip kind={step.kind} />}
                {isRunning && (
                  <span className="flex items-center gap-1 font-mono text-[10px] text-accent-text">
                    <Zap size={10} />
                    running
                  </span>
                )}
              </div>
              {step.sub && (
                <div
                  className={[
                    'font-mono text-[10.5px] mt-0.5 flex gap-2 flex-wrap',
                    isFailed ? 'text-danger' : 'text-faint',
                  ].join(' ')}
                >
                  {step.sub}
                </div>
              )}
            </div>

            {/* Right meta */}
            {step.meta != null && (
              <div
                className={[
                  'text-right font-mono text-[11px] whitespace-nowrap',
                  isFailed ? 'text-danger' : 'text-muted',
                ].join(' ')}
              >
                {step.meta}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
